import { useRef, useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Printer,
  AlertTriangle,
  Info,
  CheckCircle2,
  CalendarDays,
  Download,
  Loader2,
} from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { CHEAT_SHEET_BY_ID } from "@/data/cheatSheets";
import { track, trackPdfDownload } from "@/lib/track";
import CheatSheetA3 from "@/pages/CheatSheetA3";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const formatReviewed = (ym) => {
  if (typeof ym !== "string") return null;
  const m = ym.match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  return `${MONTHS[parseInt(m[2], 10) - 1]} ${m[1]}`;
};

/* Individual cheat sheet renderer. Handles three section types:
   - "table"   → responsive table with sticky header
   - "notes"   → bullet list of short clinical tips
   - "callout" → warning / info / success card
   Includes a print button that triggers window.print(). The @media print
   CSS in this file hides the header + print button and expands tables. */
export default function CheatSheetPage() {
  const { sheetId } = useParams();
  const navigate = useNavigate();
  const sheet = CHEAT_SHEET_BY_ID[sheetId];
  const areaRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const viewFiredRef = useRef(null);

  // Fire a `cheat_sheet_view` event exactly once per (sheetId × mount) so
  // the admin dashboard can measure which crib sheets get real usage.
  // NOTE: A3 sheets delegate rendering to <CheatSheetA3>, which fires its own
  // cheat_sheet_view. Skip here to prevent double-instrumentation
  // (iteration_57 regression fix).
  useEffect(() => {
    if (!sheet) return;
    if (sheet.layout === "a3") return;
    if (viewFiredRef.current === sheet.id) return;
    viewFiredRef.current = sheet.id;
    track("cheat_sheet_view", { sheet_id: sheet.id });
  }, [sheet]);

  const savePdf = async () => {
    if (pdfBusy || !sheet) return;
    setPdfBusy(true);
    // Optimistically log the intent; a mid-flow html2pdf failure below
    // still means "user tried to download this sheet".
    trackPdfDownload(sheet.id);
    try {
      const html2pdfMod = await import("html2pdf.js");
      const html2pdf = html2pdfMod.default || html2pdfMod;
      const element = areaRef.current;
      if (!element) return;
      await html2pdf()
        .set({
          margin: 10,
          filename: `${sheet.id}-cheat-sheet.pdf`,
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, letterRendering: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"] },
        })
        .from(element)
        .save();
    } catch {
      // Fallback to native print → Save as PDF
      window.print();
    } finally {
      setPdfBusy(false);
    }
  };

  if (!sheet) {
    return (
      <div className="min-h-screen bg-[#F4F7F9] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-slate-900">Sheet not found</h1>
          <p className="mt-2 text-slate-600">This cheat sheet doesn't exist.</p>
          <Link
            to="/cheat-sheets"
            data-testid="cheat-sheet-notfound-back"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#0047AB] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#003a8f]"
          >
            <ArrowLeft className="w-4 h-4" /> All sheets
          </Link>
        </div>
      </div>
    );
  }

  const Icon = sheet.Icon;
  const reviewed = formatReviewed(sheet.lastReviewed);

  // Densely-packed A3-landscape sheets dispatch to a dedicated renderer that
  // switches to a 3-column layout and primes the print dialog to A3 landscape.
  if (sheet.layout === "a3") {
    return <CheatSheetA3 sheet={sheet} />;
  }

  return (
    <div className="min-h-screen bg-[#F4F7F9] print:bg-white">
      {/* print stylesheet */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          table { page-break-inside: avoid; }
          h2 { page-break-after: avoid; }
        }
      `}</style>

      <header className="border-b border-slate-200 bg-white/70 backdrop-blur-sm no-print">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/sections" className="flex items-center gap-3" data-testid="brand-logo-home">
            <BrandLogo chip />
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={savePdf}
              disabled={pdfBusy}
              data-testid="cheat-sheet-pdf-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0047AB] text-white px-3.5 py-1.5 text-xs font-semibold hover:bg-[#003a8f] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              {pdfBusy ? "Building…" : "Download PDF"}
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="cheat-sheet-print-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <button
              type="button"
              onClick={() => navigate("/cheat-sheets")}
              data-testid="cheat-sheet-back"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#0047AB]"
            >
              <ArrowLeft className="w-4 h-4" /> All sheets
            </button>
          </div>
        </div>
      </header>

      <main ref={areaRef} className="max-w-4xl mx-auto px-6 py-10 print-area" data-testid={`cheat-sheet-${sheet.id}`}>
        <div className="flex items-start gap-3 mb-4">
          <div
            className="w-11 h-11 rounded-lg text-white flex items-center justify-center shrink-0"
            style={{ background: sheet.accent }}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="text-[11px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: sheet.accent }}
            >
              Sheet {String(sheet.n).padStart(2, "0")} · Quick reference
            </p>
            <h1 className="mt-0.5 text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
              {sheet.title}
            </h1>
            <p className="mt-1 text-sm text-slate-600 max-w-2xl leading-relaxed">{sheet.blurb}</p>
            {reviewed && (
              <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                <CalendarDays className="w-3.5 h-3.5" />
                Last reviewed {reviewed}
              </p>
            )}
          </div>
        </div>

        <div className="mt-6 space-y-8">
          {sheet.sections.map((sec, i) => (
            <SectionBlock key={i} sec={sec} accent={sheet.accent} idx={i} />
          ))}
        </div>

        <div className="mt-12 text-center text-[11px] text-slate-400 no-print">
          Not a substitute for the drug label or your practice formulary · Always confirm doses against the patient's current weight and bloods.
        </div>
      </main>
    </div>
  );
}

function SectionBlock({ sec, accent, idx }) {
  if (sec.type === "table") {
    return (
      <section data-testid={`cheat-section-${idx}`}>
        <h2 className="text-lg font-semibold text-slate-900 mb-3">{sec.heading}</h2>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {sec.columns.map((c, i) => (
                  <th
                    key={i}
                    className="text-left px-4 py-2.5 font-semibold text-[11px] uppercase tracking-wider"
                    style={{ color: accent }}
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sec.rows.map((row, ri) => (
                <tr key={ri} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className={`px-4 py-2.5 ${ci === 0 ? "font-semibold text-slate-900" : "text-slate-700"}`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sec.foot && (
          <p className="mt-2 text-xs text-slate-500 italic leading-relaxed">{sec.foot}</p>
        )}
      </section>
    );
  }

  if (sec.type === "notes") {
    return (
      <section data-testid={`cheat-section-${idx}`}>
        <h2 className="text-lg font-semibold text-slate-900 mb-3">{sec.heading}</h2>
        <ul className="space-y-2 rounded-xl border border-slate-200 bg-white p-5">
          {sec.items.map((t, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed">
              <span
                className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                style={{ background: accent }}
                aria-hidden
              />
              {t}
            </li>
          ))}
        </ul>
      </section>
    );
  }

  if (sec.type === "callout") {
    const map = {
      warning: { Icon: AlertTriangle, bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-900" },
      info:    { Icon: Info,          bg: "bg-sky-50",   border: "border-sky-300",   text: "text-sky-900" },
      success: { Icon: CheckCircle2,  bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-900" },
    };
    const cfg = map[sec.tone] || map.info;
    const CIcon = cfg.Icon;
    return (
      <section data-testid={`cheat-section-${idx}`}>
        <div className={`rounded-xl border ${cfg.border} ${cfg.bg} p-5 flex items-start gap-3 ${cfg.text}`}>
          <CIcon className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold">{sec.heading}</h3>
            <p className="mt-1 text-sm leading-relaxed">{sec.body}</p>
          </div>
        </div>
      </section>
    );
  }

  return null;
}
